import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Download, CheckCircle, Phone, Settings, RotateCw, Zap, Mail, Filter, AlertTriangle } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import Navbar from '../components/Navbar';
import SEOHead from '../components/SEOHead';
import Footer from '../components/Footer';
import FiltrationCTABanner from '../components/FiltrationCTABanner';

const BrushCleaningPage = () => {
  const location = useLocation();
  const [activeTab, setActiveTab] = useState<'manual' | 'automatic'>('manual');
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);

  useEffect(() => {
    if (location.hash === '#automatic') {
      setActiveTab('automatic');
    }
  }, [location]);

  const toggleFAQ = (index: number) => {
    setOpenFAQ(openFAQ === index ? null : index);
  };

  const faqs = [
    {
      question: "What's the difference between manual (BIF) and automatic (RAB) brush cleaning?",
      answer: "Manual BIF (Brush In Filter) requires an operator to turn a hand crank to initiate cleaning cycles. The operator decides when cleaning is needed based on visual inspection or pressure gauges. Automatic RAB (Rotating Automatic Brush) uses a differential pressure sensor or timer to automatically trigger cleaning cycles without operator intervention. RAB is ideal for remote sites or applications requiring consistent maintenance without manual labor."
    },
    {
      question: "What differential pressure (ΔP) setpoint should I use for RAB?",
      answer: "Typical ΔP setpoints range from 5-15 PSI depending on your application, flow rate, and mesh size. Finer mesh screens (100-120 mesh) may trigger cleaning at lower ΔP (5-8 PSI), while coarser screens (20-50 mesh) can tolerate higher ΔP (10-15 PSI) before cleaning. Your dealer or CTC engineering can help determine the optimal setpoint for your water quality and system design."
    },
    {
      question: "How often do brushes need replacement?",
      answer: "Brush wear depends on cleaning frequency, screen abrasiveness, and solids loading. In typical agricultural applications with moderate solids, brushes last 1-3 years. Inspect brushes annually for excessive wear—bristles should maintain adequate stiffness and coverage. Replacement brush assemblies are available for all BIG Series sizes and can be changed in the field without special tools."
    },
    {
      question: "Can I retrofit from manual (BIF) to automatic (RAB)?",
      answer: "Yes! The BIG Series platform is designed for field retrofits. The RAB automatic brush module mounts directly to the existing BIG filter body—no repiping or welding required. You'll need to add 120V or 240V power and install the differential pressure sensor, but the mechanical installation is straightforward. Contact your dealer for retrofit kit availability."
    },
    {
      question: "What are typical service intervals for brush cleaning systems?",
      answer: "Manual BIF systems require operator-initiated cleaning cycles—frequency depends on water quality and can range from daily to weekly. Automatic RAB systems handle cleaning automatically based on ΔP or timer settings. Routine maintenance includes quarterly brush inspection, annual gasket replacement, and screen inspection. Clean water applications require less frequent service than heavy debris loads."
    },
    {
      question: "What mesh sizes work best with brush cleaning?",
      answer: "Brush cleaning excels with 20-80 mesh screens for drip and micro-irrigation. Finer mesh (100-120) can be used but may require more frequent cleaning cycles. The rotating brush action is particularly effective for removing fibrous organic matter, algae, and soft debris that can adhere to screens. For extremely fine mesh or heavy sand/silt loads, consider vacuum cleaning technology instead."
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="BIG Brush Cleaning Filters | BIF (Manual) & RAB (Automatic) | CTC Waterworks"
        description="Brush cleaning technology page with full specs, models/kits, and parts. Manual BIF and automatic RAB options for BIG Series inline filters."
        canonical="https://www.ctcwaterworks.com/filtration/inline/big/brush-cleaning"
      />
      <Navbar />
      <FiltrationCTABanner />

      {/* Breadcrumbs */}
      <nav aria-label="Breadcrumb" className="bg-gray-50 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-3">
          <ol className="flex items-center space-x-2 text-sm">
            <li>
              <Link to="/" className="text-gray-600 hover:text-ctc-blue">Home</Link>
            </li>
            <li className="text-gray-400">/</li>
            <li>
              <Link to="/filtration" className="text-gray-600 hover:text-ctc-blue">Filtration</Link>
            </li>
            <li className="text-gray-400">/</li>
            <li>
              <Link to="/filtration/inline/big" className="text-gray-600 hover:text-ctc-blue">BIG Series</Link>
            </li>
            <li className="text-gray-400">/</li>
            <li className="text-gray-900 font-semibold">Brush Cleaning</li>
          </ol>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-16 pb-12 bg-gradient-to-br from-green-50 to-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Main Content - Left 2/3 */}
            <div className="lg:col-span-2">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <div className="flex items-center mb-4">
                  <RotateCw className="text-green-600 mr-4" size={48} />
                  <div>
                    <h1 className="text-5xl font-bold text-gray-900">
                      BIG—Brush Cleaning Technology
                    </h1>
                  </div>
                </div>
                <p className="text-2xl text-gray-600 mb-8 font-light leading-relaxed">
                  Manual (BIF) and Automatic (RAB) brush filters for fibrous/organic solids.
                </p>
              </motion.div>
            </div>

            {/* Right Rail - Downloads & CTAs */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-lg shadow-xl p-6 space-y-4 sticky top-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Resources & Support</h3>

                <Link
                  to="/contact?topic=filtration"
                  className="block w-full bg-green-600 text-white px-6 py-4 rounded-lg font-semibold hover:bg-green-700 transition-colors text-center shadow-lg"
                >
                  <Mail className="inline-block mr-2 mb-1" size={20} />
                  Talk to a Filtration Expert
                </Link>

                <div className="space-y-2 pt-4 border-t border-gray-200">
                  <h4 className="font-semibold text-gray-900 text-sm mb-2">Download Resources</h4>

                  <a
                    href="#"
                    className="flex items-center text-sm text-gray-700 hover:text-green-600 p-2 rounded hover:bg-gray-50 transition-colors"
                    onClick={(e) => e.preventDefault()}
                  >
                    <Download size={16} className="mr-2 flex-shrink-0" />
                    <span>Brush Cleaning Sell Sheet (PDF)</span>
                  </a>

                  <a
                    href="#"
                    className="flex items-center text-sm text-gray-700 hover:text-green-600 p-2 rounded hover:bg-gray-50 transition-colors"
                    onClick={(e) => e.preventDefault()}
                  >
                    <Download size={16} className="mr-2 flex-shrink-0" />
                    <span>Dimensional Drawing (PDF)</span>
                  </a>

                  <a
                    href="#"
                    className="flex items-center text-sm text-gray-700 hover:text-green-600 p-2 rounded hover:bg-gray-50 transition-colors"
                    onClick={(e) => e.preventDefault()}
                  >
                    <Download size={16} className="mr-2 flex-shrink-0" />
                    <span>Spec Table (CSV)</span>
                  </a>
                </div>

                <div className="pt-4 border-t border-gray-200">
                  <div className="flex items-center space-x-3 text-gray-700 mb-3">
                    <Phone size={20} />
                    <span className="text-sm">(509) 901-7476</span>
                  </div>
                  <div className="flex items-center space-x-3 text-gray-700">
                    <Mail size={20} />
                    <a href="mailto:Andre@ctcwaterworks.com" className="text-sm hover:text-green-600 underline">
                      Andre@ctcwaterworks.com
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Product Images Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
              <img src="/BIF.jpeg" alt="BIF Brush-in-Filter Product" className="w-full h-auto" />
              <div className="p-4">
                <h3 className="font-bold text-gray-900 mb-2">BIF Manual Brush System</h3>
                <p className="text-sm text-gray-700">Hand-crank operated brush cleaning for manual operation</p>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
              <img src="/RAB.jpeg" alt="RAB Automatic Brush System" className="w-full h-auto" />
              <div className="p-4">
                <h3 className="font-bold text-gray-900 mb-2">RAB Automatic Brush System</h3>
                <p className="text-sm text-gray-700">Motorized automatic brush cleaning with ΔP control</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works - Shared Section */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">How Brush Cleaning Works</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
            <div className="flex justify-center items-center">
              <img src="/BIF Diagram.jpeg" alt="BIF Technical Diagram" className="max-w-full h-auto" style={{maxWidth: '400px'}} />
            </div>
            <div className="flex justify-center items-center">
              <img src="/RAB Internal.jpeg" alt="RAB/BIF Internal Components" className="max-w-full h-auto" style={{maxWidth: '400px'}} />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
            <div className="bg-gradient-to-br from-green-50 to-white border-2 border-green-300 rounded-lg p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                <RotateCw className="mr-3 text-green-600" size={28} />
                Mechanical Action
              </h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                Internal rotating brush assembly continuously scrubs the screen surface during operation. Bristles mechanically dislodge and remove fibrous debris, organic matter, algae, and soft particles that accumulate on the filter screen.
              </p>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <CheckCircle className="mr-2 text-green-600 mt-1 flex-shrink-0" size={18} />
                  <span className="text-sm text-gray-700">Brushes rotate inside filter body</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="mr-2 text-green-600 mt-1 flex-shrink-0" size={18} />
                  <span className="text-sm text-gray-700">Bristles scrub screen clean from inside</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="mr-2 text-green-600 mt-1 flex-shrink-0" size={18} />
                  <span className="text-sm text-gray-700">Debris exits via purge valve</span>
                </li>
              </ul>
            </div>

            <div className="bg-gradient-to-br from-blue-50 to-white border-2 border-blue-300 rounded-lg p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                <Zap className="mr-3 text-blue-600" size={28} />
                Control Options
              </h3>
              <div className="space-y-4">
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Manual (BIF):</h4>
                  <p className="text-gray-700 text-sm leading-relaxed">
                    Operator turns hand crank to initiate cleaning cycle. Simple mechanical operation—no power or controls required. Clean as needed based on visual inspection or pressure gauge.
                  </p>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Automatic (RAB):</h4>
                  <p className="text-gray-700 text-sm leading-relaxed">
                    Differential pressure (ΔP) sensor or timer automatically triggers motor-driven cleaning cycles. Set ΔP threshold (typically 5-15 PSI) or timer interval for hands-free operation.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-blue-50 border-l-4 border-green-600 p-6 rounded-r-lg">
            <p className="text-gray-700 leading-relaxed">
              <span className="font-semibold text-gray-900">Best For:</span> Surface water, canal water, ponds, and wells with fibrous organic debris, algae, and moderate solids loading. The mechanical brush action excels at removing sticky organic matter that adheres to screens.
            </p>
          </div>
        </div>
      </section>

      {/* Tabs Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          {/* Tab Navigation */}
          <div className="flex justify-center mb-8">
            <div className="bg-white rounded-lg shadow-lg p-2 inline-flex">
              <button
                onClick={() => setActiveTab('manual')}
                className={`px-8 py-4 rounded-lg font-semibold transition-colors ${
                  activeTab === 'manual'
                    ? 'bg-green-600 text-white'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                Manual (BIF / IMB)
              </button>
              <button
                onClick={() => setActiveTab('automatic')}
                id="automatic"
                className={`px-8 py-4 rounded-lg font-semibold transition-colors ${
                  activeTab === 'automatic'
                    ? 'bg-green-600 text-white'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                Automatic (RAB)
              </button>
            </div>
          </div>

          {/* Tab Content */}
          {activeTab === 'manual' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
            >
              <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Manual Brush Cleaning (BIF / IMB)</h2>
                <p className="text-gray-700 leading-relaxed mb-6">
                  BIF (Brush In Filter) and IMB (Internal Manual Brush) are semi-automatic systems requiring operator-initiated cleaning. Turn the hand crank to rotate brushes through the filter screen, scrubbing it clean while maintaining system flow. Ideal for accessible sites where periodic manual maintenance is acceptable.
                </p>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  <div className="bg-green-50 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-900 mb-2">Simple Operation</h4>
                    <p className="text-sm text-gray-700">Hand-crank initiated cleaning cycles</p>
                  </div>
                  <div className="bg-green-50 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-900 mb-2">No Power Required</h4>
                    <p className="text-sm text-gray-700">Fully mechanical—no electrical connection needed</p>
                  </div>
                  <div className="bg-green-50 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-900 mb-2">Low Maintenance</h4>
                    <p className="text-sm text-gray-700">Minimal moving parts, simple mechanical design</p>
                  </div>
                </div>


                <h3 className="text-xl font-bold text-gray-900 mb-4">BIF/IMB Model & Kits Matrix</h3>
                <div className="overflow-x-auto mb-6">
                  <table className="w-full border-collapse bg-white">
                    <thead className="bg-gradient-to-r from-green-600 to-green-700 text-white">
                      <tr>
                        <th className="px-4 py-3 text-left font-semibold">Flow (gpm)</th>
                        <th className="px-4 py-3 text-left font-semibold">Manual Model (BIF)</th>
                        <th className="px-4 py-3 text-left font-semibold">Automatic Model (RAB)</th>
                        <th className="px-4 py-3 text-left font-semibold">Brush Assembly</th>
                        <th className="px-4 py-3 text-left font-semibold">Screen Part #</th>
                        <th className="px-4 py-3 text-left font-semibold">Screen Length</th>
                        <th className="px-4 py-3 text-left font-semibold">Mesh</th>
                        <th className="px-4 py-3 text-left font-semibold">Perf</th>
                        <th className="px-4 py-3 text-left font-semibold">Notes</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      <tr className="hover:bg-green-50">
                        <td className="px-4 py-3 font-semibold">500</td>
                        <td className="px-4 py-3 text-sm">BA752</td>
                        <td className="px-4 py-3 text-sm">—</td>
                        <td className="px-4 py-3 text-sm">BA613</td>
                        <td className="px-4 py-3 text-sm">A3929</td>
                        <td className="px-4 py-3 text-sm">24"</td>
                        <td className="px-4 py-3 text-sm">80</td>
                        <td className="px-4 py-3 text-sm">.032</td>
                        <td className="px-4 py-3 text-sm">Compact</td>
                      </tr>
                      <tr className="hover:bg-green-50">
                        <td className="px-4 py-3 font-semibold">800</td>
                        <td className="px-4 py-3 text-sm">BA592</td>
                        <td className="px-4 py-3 text-sm">—</td>
                        <td className="px-4 py-3 text-sm">BA411</td>
                        <td className="px-4 py-3 text-sm">A3220</td>
                        <td className="px-4 py-3 text-sm">36"</td>
                        <td className="px-4 py-3 text-sm">80</td>
                        <td className="px-4 py-3 text-sm">.032</td>
                        <td className="px-4 py-3 text-sm">Standard</td>
                      </tr>
                      <tr className="hover:bg-green-50">
                        <td className="px-4 py-3 font-semibold">1000</td>
                        <td className="px-4 py-3 text-sm">BA591</td>
                        <td className="px-4 py-3 text-sm">BA517</td>
                        <td className="px-4 py-3 text-sm">BA587</td>
                        <td className="px-4 py-3 text-sm">A3221</td>
                        <td className="px-4 py-3 text-sm">48"</td>
                        <td className="px-4 py-3 text-sm">60</td>
                        <td className="px-4 py-3 text-sm">.032</td>
                        <td className="px-4 py-3 text-sm">Auto upgrade</td>
                      </tr>
                      <tr className="hover:bg-green-50">
                        <td className="px-4 py-3 font-semibold">1200</td>
                        <td className="px-4 py-3 text-sm">BA590</td>
                        <td className="px-4 py-3 text-sm">BA590</td>
                        <td className="px-4 py-3 text-sm">BA496</td>
                        <td className="px-4 py-3 text-sm">B3222</td>
                        <td className="px-4 py-3 text-sm">60"</td>
                        <td className="px-4 py-3 text-sm">60</td>
                        <td className="px-4 py-3 text-sm">.032</td>
                        <td className="px-4 py-3 text-sm">—</td>
                      </tr>
                      <tr className="hover:bg-green-50">
                        <td className="px-4 py-3 font-semibold">1500</td>
                        <td className="px-4 py-3 text-sm">BA589</td>
                        <td className="px-4 py-3 text-sm">BA629</td>
                        <td className="px-4 py-3 text-sm">BA495</td>
                        <td className="px-4 py-3 text-sm">B3223</td>
                        <td className="px-4 py-3 text-sm">90"</td>
                        <td className="px-4 py-3 text-sm">60</td>
                        <td className="px-4 py-3 text-sm">.032</td>
                        <td className="px-4 py-3 text-sm">—</td>
                      </tr>
                      <tr className="hover:bg-green-50">
                        <td className="px-4 py-3 font-semibold">2000</td>
                        <td className="px-4 py-3 text-sm">BA629</td>
                        <td className="px-4 py-3 text-sm">—</td>
                        <td className="px-4 py-3 text-sm">BA612</td>
                        <td className="px-4 py-3 text-sm">B3982</td>
                        <td className="px-4 py-3 text-sm">120"</td>
                        <td className="px-4 py-3 text-sm">60</td>
                        <td className="px-4 py-3 text-sm">.032</td>
                        <td className="px-4 py-3 text-sm">Max flow</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div className="bg-blue-50 border-l-4 border-green-600 p-4 rounded-r-lg">
                  <p className="text-sm text-gray-700">
                    <span className="font-semibold">Note:</span> All BIF models include complete filter assembly with head, body, manual brush mechanism, hand crank, and one standard screen. Custom sizes and configurations available—contact engineering for details.
                  </p>
                </div>
              </div>
            </motion.div>
          )}

          {activeTab === 'automatic' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
            >
              <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Automatic Brush Cleaning (RAB)</h2>
                <p className="text-gray-700 leading-relaxed mb-6">
                  RAB (Rotating Automatic Brush) systems use electric motor drive with differential pressure sensors or timers to automatically initiate cleaning cycles without operator intervention. Perfect for remote sites, continuous operations, or applications requiring unattended maintenance.
                </p>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  <div className="bg-green-50 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-900 mb-2">Fully Automatic</h4>
                    <p className="text-sm text-gray-700">ΔP sensor or timer-triggered cleaning</p>
                  </div>
                  <div className="bg-green-50 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-900 mb-2">Remote Ready</h4>
                    <p className="text-sm text-gray-700">No operator presence required</p>
                  </div>
                  <div className="bg-green-50 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-900 mb-2">Consistent ΔP</h4>
                    <p className="text-sm text-gray-700">Maintains optimal pressure differential</p>
                  </div>
                </div>

                <p className="text-gray-700 leading-relaxed mb-6">
                  See model matrix above for RAB automatic options. Models 1000 GPM and larger offer automatic RAB configurations with motor-driven brush rotation and ΔP sensor control.
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  <div className="bg-blue-50 border-l-4 border-green-600 p-4 rounded-r-lg">
                    <h4 className="font-semibold text-gray-900 mb-2">Power Requirements</h4>
                    <p className="text-sm text-gray-700">
                      Standard 120V or 240V AC power. Motor draws minimal current during cleaning cycles. Can integrate with existing control panels or SCADA systems.
                    </p>
                  </div>
                  <div className="bg-blue-50 border-l-4 border-green-600 p-4 rounded-r-lg">
                    <h4 className="font-semibold text-gray-900 mb-2">Control Options</h4>
                    <p className="text-sm text-gray-700">
                      Differential pressure sensor (adjustable 5-15 PSI setpoint) or timer-based control. Hybrid mode combines both for maximum flexibility.
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </div>
      </section>

      {/* Shared Spec Table */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">BIG Series Brush Cleaning Specifications</h2>
          <p className="text-center text-gray-600 mb-8 max-w-3xl mx-auto">
            Common specifications for both manual (BIF) and automatic (RAB) brush cleaning systems. Dimensions and flow rates are identical—only the cleaning mechanism differs.
          </p>

          <div className="overflow-x-auto bg-white rounded-lg shadow-lg">
            <table className="w-full border-collapse">
              <thead className="bg-gradient-to-r from-green-600 to-green-700 text-white">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold">Model</th>
                  <th className="px-4 py-3 text-left font-semibold">Size (nominal)</th>
                  <th className="px-4 py-3 text-left font-semibold">Connection</th>
                  <th className="px-4 py-3 text-left font-semibold">Flow Range (gpm)</th>
                  <th className="px-4 py-3 text-left font-semibold">Screen Length (in)</th>
                  <th className="px-4 py-3 text-left font-semibold">Standard Mesh/Perf</th>
                  <th className="px-4 py-3 text-left font-semibold">Approx. Weight (lb)</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                <tr className="hover:bg-green-50">
                  <td className="px-4 py-3 font-semibold">BIF/RAB-2</td>
                  <td className="px-4 py-3">2"</td>
                  <td className="px-4 py-3">2" NPT</td>
                  <td className="px-4 py-3">20-80</td>
                  <td className="px-4 py-3">18</td>
                  <td className="px-4 py-3">20-120 mesh</td>
                  <td className="px-4 py-3">45</td>
                </tr>
                <tr className="hover:bg-green-50">
                  <td className="px-4 py-3 font-semibold">BIF/RAB-3</td>
                  <td className="px-4 py-3">3"</td>
                  <td className="px-4 py-3">3" NPT</td>
                  <td className="px-4 py-3">50-150</td>
                  <td className="px-4 py-3">24</td>
                  <td className="px-4 py-3">20-120 mesh</td>
                  <td className="px-4 py-3">75</td>
                </tr>
                <tr className="hover:bg-green-50">
                  <td className="px-4 py-3 font-semibold">BIF/RAB-4</td>
                  <td className="px-4 py-3">4"</td>
                  <td className="px-4 py-3">4" NPT</td>
                  <td className="px-4 py-3">100-300</td>
                  <td className="px-4 py-3">30</td>
                  <td className="px-4 py-3">20-120 mesh</td>
                  <td className="px-4 py-3">125</td>
                </tr>
                <tr className="hover:bg-green-50">
                  <td className="px-4 py-3 font-semibold">BIF/RAB-6</td>
                  <td className="px-4 py-3">6"</td>
                  <td className="px-4 py-3">6" Flange</td>
                  <td className="px-4 py-3">300-700</td>
                  <td className="px-4 py-3">36</td>
                  <td className="px-4 py-3">20-100 mesh</td>
                  <td className="px-4 py-3">250</td>
                </tr>
                <tr className="hover:bg-green-50">
                  <td className="px-4 py-3 font-semibold">BIF/RAB-8</td>
                  <td className="px-4 py-3">8"</td>
                  <td className="px-4 py-3">8" Flange</td>
                  <td className="px-4 py-3">500-1200</td>
                  <td className="px-4 py-3">42</td>
                  <td className="px-4 py-3">20-80 mesh</td>
                  <td className="px-4 py-3">400</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="mt-6 bg-blue-50 border-l-4 border-green-600 p-6 rounded-r-lg max-w-4xl mx-auto">
            <p className="text-gray-700 leading-relaxed">
              <span className="font-semibold text-gray-900">Important:</span> Flow ranges are guidelines for typical installations. Actual capacity depends on screen mesh size, debris loading, and acceptable head loss. Contact engineering for application-specific sizing. Custom sizes beyond 8" available for high-flow applications.
            </p>
          </div>
        </div>
      </section>

      {/* Replacement Parts Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Replacement Parts & Service Components</h2>

          <div className="bg-white rounded-lg shadow-lg overflow-hidden mb-8">
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead className="bg-gradient-to-r from-gray-700 to-gray-800 text-white">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold">Flow (gpm)</th>
                    <th className="px-4 py-3 text-left font-semibold">Body</th>
                    <th className="px-4 py-3 text-left font-semibold">Head</th>
                    <th className="px-4 py-3 text-left font-semibold">Screen (Size × Length)</th>
                    <th className="px-4 py-3 text-left font-semibold">Mesh</th>
                    <th className="px-4 py-3 text-left font-semibold">Brush Type</th>
                    <th className="px-4 py-3 text-left font-semibold">Notes</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  <tr className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-semibold">500</td>
                    <td className="px-4 py-3 text-sm">B3931</td>
                    <td className="px-4 py-3 text-sm">A3927</td>
                    <td className="px-4 py-3 text-sm">4" × 24"</td>
                    <td className="px-4 py-3 text-sm">80</td>
                    <td className="px-4 py-3 text-sm">BA613</td>
                    <td className="px-4 py-3 text-sm">IMB compatible</td>
                  </tr>
                  <tr className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-semibold">800</td>
                    <td className="px-4 py-3 text-sm">B3196</td>
                    <td className="px-4 py-3 text-sm">A3195</td>
                    <td className="px-4 py-3 text-sm">6" × 36"</td>
                    <td className="px-4 py-3 text-sm">80</td>
                    <td className="px-4 py-3 text-sm">BA411</td>
                    <td className="px-4 py-3 text-sm">—</td>
                  </tr>
                  <tr className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-semibold">1000</td>
                    <td className="px-4 py-3 text-sm">B3210</td>
                    <td className="px-4 py-3 text-sm">B3209</td>
                    <td className="px-4 py-3 text-sm">8" × 48"</td>
                    <td className="px-4 py-3 text-sm">60</td>
                    <td className="px-4 py-3 text-sm">BA587</td>
                    <td className="px-4 py-3 text-sm">—</td>
                  </tr>
                  <tr className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-semibold">1200</td>
                    <td className="px-4 py-3 text-sm">B3185</td>
                    <td className="px-4 py-3 text-sm">B3173</td>
                    <td className="px-4 py-3 text-sm">10" × 60"</td>
                    <td className="px-4 py-3 text-sm">60</td>
                    <td className="px-4 py-3 text-sm">BA496</td>
                    <td className="px-4 py-3 text-sm">—</td>
                  </tr>
                  <tr className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-semibold">1500</td>
                    <td className="px-4 py-3 text-sm">B3746</td>
                    <td className="px-4 py-3 text-sm">B3173</td>
                    <td className="px-4 py-3 text-sm">10" × 90"</td>
                    <td className="px-4 py-3 text-sm">60</td>
                    <td className="px-4 py-3 text-sm">BA495</td>
                    <td className="px-4 py-3 text-sm">—</td>
                  </tr>
                  <tr className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-semibold">2000</td>
                    <td className="px-4 py-3 text-sm">B3969</td>
                    <td className="px-4 py-3 text-sm">B3173</td>
                    <td className="px-4 py-3 text-sm">10" × 120"</td>
                    <td className="px-4 py-3 text-sm">60</td>
                    <td className="px-4 py-3 text-sm">BA612</td>
                    <td className="px-4 py-3 text-sm">—</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div className="bg-blue-50 border-l-4 border-ctc-blue p-6 rounded-r-lg max-w-4xl mx-auto">
            <p className="text-gray-700 leading-relaxed">
              <span className="font-semibold text-gray-900">Parts Availability:</span> All replacement parts are stocked by authorized dealers. Brush assemblies, screens, bodies, and heads are field-serviceable. For part numbers, availability, and pricing, contact your local dealer or CTC Waterworks engineering.
            </p>
          </div>
        </div>
      </section>

      {/* Mesh/Perf Quick Guide - Sidebar Component */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Mesh & Perforation Quick Guide</h2>
          <p className="text-center text-gray-600 mb-8 max-w-3xl mx-auto">
            Typical starting points for common applications. Brush cleaning works best with 20-80 mesh for most irrigation applications.
          </p>

          <div className="bg-white rounded-lg shadow-lg overflow-hidden">
            <table className="w-full">
              <thead className="bg-gradient-to-r from-green-600 to-green-700 text-white">
                <tr>
                  <th className="px-6 py-4 text-left font-semibold">Application</th>
                  <th className="px-6 py-4 text-left font-semibold">Recommended Mesh</th>
                  <th className="px-6 py-4 text-left font-semibold">Micron Range</th>
                  <th className="px-6 py-4 text-left font-semibold">Notes</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                <tr className="hover:bg-green-50">
                  <td className="px-6 py-4 font-semibold text-gray-900">Drip Irrigation</td>
                  <td className="px-6 py-4 text-gray-700">80-120 mesh</td>
                  <td className="px-6 py-4 text-gray-600">125-177 µm</td>
                  <td className="px-6 py-4 text-sm text-gray-600">Fine mesh for emitter protection</td>
                </tr>
                <tr className="hover:bg-green-50">
                  <td className="px-6 py-4 font-semibold text-gray-900">Micro-Sprinkler</td>
                  <td className="px-6 py-4 text-gray-700">50-80 mesh</td>
                  <td className="px-6 py-4 text-gray-600">177-297 µm</td>
                  <td className="px-6 py-4 text-sm text-gray-600">Ideal for brush cleaning</td>
                </tr>
                <tr className="hover:bg-green-50">
                  <td className="px-6 py-4 font-semibold text-gray-900">Center Pivot</td>
                  <td className="px-6 py-4 text-gray-700">30-50 mesh</td>
                  <td className="px-6 py-4 text-gray-600">297-595 µm</td>
                  <td className="px-6 py-4 text-sm text-gray-600">Coarse mesh, high flow</td>
                </tr>
                <tr className="hover:bg-green-50">
                  <td className="px-6 py-4 font-semibold text-gray-900">Intake Polishing</td>
                  <td className="px-6 py-4 text-gray-700">20-40 mesh or 1/16" perf</td>
                  <td className="px-6 py-4 text-gray-600">420-840 µm</td>
                  <td className="px-6 py-4 text-sm text-gray-600">Pre-filtration for pump protection</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="mt-6 bg-green-50 border-l-4 border-green-600 p-6 rounded-r-lg max-w-4xl mx-auto">
            <p className="text-gray-700 leading-relaxed">
              <span className="font-semibold text-gray-900">Custom Mesh Sizes:</span> Not limited to standard offerings. Custom mesh sizes and perforation patterns available to match your specific emitter requirements and water quality. Contact engineering for recommendations.
            </p>
          </div>
        </div>
      </section>

      {/* Cross-Links */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Compare Other Technologies</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Link
              to="/filtration/inline/big/vacuum-cleaning"
              className="bg-white border-2 border-orange-300 rounded-lg p-6 hover:shadow-xl transition-shadow"
            >
              <div className="flex items-center mb-3">
                <Filter className="text-orange-600 mr-3" size={32} />
                <h3 className="text-xl font-bold text-gray-900">Vacuum Cleaning</h3>
              </div>
              <p className="text-gray-700 mb-4 text-sm leading-relaxed">
                Compare vacuum cleaning technology (MVC/AVC) for heavy sand/silt loads and fine mesh applications. Rapid backflush cycles with minimal water loss.
              </p>
              <div className="flex items-center text-orange-600 font-semibold">
                View Vacuum Cleaning
                <ArrowRight className="ml-2" size={18} />
              </div>
            </Link>

            <Link
              to="/filtration/self-cleaning"
              className="bg-white border-2 border-blue-300 rounded-lg p-6 hover:shadow-xl transition-shadow"
            >
              <div className="flex items-center mb-3">
                <Settings className="text-ctc-blue mr-3" size={32} />
                <h3 className="text-xl font-bold text-gray-900">Self-Cleaning Overview</h3>
              </div>
              <p className="text-gray-700 mb-4 text-sm leading-relaxed">
                Detailed comparison of automatic brush (RAB) vs automatic vacuum (AVC) technologies to help you choose the right solution for your application.
              </p>
              <div className="flex items-center text-ctc-blue font-semibold">
                Compare Technologies
                <ArrowRight className="ml-2" size={18} />
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Frequently Asked Questions</h2>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-gray-50 rounded-lg shadow-md overflow-hidden">
                <button
                  onClick={() => toggleFAQ(index)}
                  className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-100 transition-colors"
                >
                  <span className="font-semibold text-gray-900 pr-4">{faq.question}</span>
                  <CheckCircle
                    className={`flex-shrink-0 text-green-600 transition-transform ${
                      openFAQ === index ? 'transform rotate-180' : ''
                    }`}
                    size={24}
                  />
                </button>
                {openFAQ === index && (
                  <div className="px-6 pb-4 bg-white">
                    <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Cleaning Options Cross-Links */}
      <section className="py-12 bg-blue-50 border-y border-blue-200">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h3 className="text-2xl font-bold text-gray-900 mb-4">Explore All Cleaning Options</h3>
          <p className="text-gray-700 mb-6">
            Brush technology is available in Semi-Automatic (BIF) and Fully Automatic (RAB) configurations for BIG, BIG-S, and GNG series filters.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/filtration/inline/cleaning/semi-auto"
              className="bg-ctc-blue text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
            >
              Semi-Auto (BIF & MVC)
            </Link>
            <Link
              to="/filtration/inline/cleaning/automatic"
              className="bg-green-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-green-700 transition-colors"
            >
              Fully Automatic (RAB & AVC)
            </Link>
            <Link
              to="/filtration/inline/cleaning"
              className="border-2 border-ctc-blue text-ctc-blue px-6 py-3 rounded-lg font-semibold hover:bg-ctc-blue hover:text-white transition-colors"
            >
              Compare All Options
            </Link>
          </div>
          <div className="mt-6 pt-6 border-t border-blue-200">
            <p className="text-sm text-gray-600">
              <AlertTriangle className="inline mr-2 text-orange-600" size={16} />
              TIG filters do not support Brush/Vacuum/RAB cleaning options.
            </p>
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-16 bg-gradient-to-br from-green-600 to-green-800 text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold mb-4">Ready to Select Your Brush Cleaning System?</h2>
          <p className="text-xl text-green-100 mb-8">
            Our engineering team will help you choose between manual and automatic brush cleaning based on your site accessibility, maintenance preferences, and operational requirements.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact?topic=filtration"
              className="bg-white text-green-600 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors shadow-lg inline-flex items-center justify-center"
            >
              <Mail className="mr-2" size={20} />
              Talk to a Filtration Expert
            </Link>

            <Link
              to="/filtration/size-my-filter"
              className="bg-green-800 text-white px-8 py-4 rounded-lg font-semibold hover:bg-green-900 transition-colors shadow-lg inline-flex items-center justify-center"
            >
              <Settings className="mr-2" size={20} />
              Size My Filter
            </Link>
          </div>

          <div className="mt-8 pt-8 border-t border-white/20">
            <div className="flex flex-col sm:flex-row items-center justify-center gap-6 text-green-100">
              <div className="flex items-center">
                <Phone className="mr-2" size={20} />
                <span>(509) 901-7476</span>
              </div>
              <div className="flex items-center">
                <Mail className="mr-2" size={20} />
                <a href="mailto:Andre@ctcwaterworks.com" className="hover:text-white underline">
                  Andre@ctcwaterworks.com
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BrushCleaningPage;
